import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import ChatInput from "./ChatInput";
import { Dropdown, Button } from "react-bootstrap";
import io from "socket.io-client";
import ChatContacts from "./ChatContacts";
import { useParams } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import { getMessageDayStatus } from "../../utilities/functions";
import { validateChatAttachment } from "./chatAttachmentConfig";

interface Message {
  id_message?: number;
  id_chat?: number;
  id_sender?: number;
  id_received: number;
  content: string;
  time: string;
  is_right: boolean;
  nom_user?: string;
  prenom_user?: string;
  img?: string;

}

interface Contact {
  id_user: number;
  nom_user: string;
  prenom_user: string;
  username_user: string;
  position: string;
  phone_number: string;
  email_user: string;
  status: string;
  img: string;
}

interface AttachmentPayload {
  type: "file";
  name: string;
  url: string;
  mimeType?: string;
  size?: number;
  text?: string;
}

const CHAT_ATTACHMENT_PREFIX = "__CHAT_FILE__:";

const normalizeMessagesPayload = (payload: unknown): Message[] => {
  if (Array.isArray(payload)) {
    return payload.map((message) => {
      if (
        message &&
        typeof message === "object" &&
        "id_ticket" in message &&
        !("id_chat" in message)
      ) {
        const legacyMessage = message as Message & { id_ticket?: number };
        return { ...legacyMessage, id_chat: legacyMessage.id_ticket };
      }

      return message as Message;
    });
  }

  console.error("Unexpected messages payload:", payload);
  return [];
};

const parseAttachmentPayload = (content: string): AttachmentPayload | null => {
  if (!content.startsWith(CHAT_ATTACHMENT_PREFIX)) {
    return null;
  }

  try {
    const parsedContent = JSON.parse(
      content.slice(CHAT_ATTACHMENT_PREFIX.length)
    ) as AttachmentPayload;

    if (
      parsedContent &&
      parsedContent.type === "file" &&
      typeof parsedContent.name === "string" &&
      typeof parsedContent.url === "string"
    ) {
      return parsedContent;
    }
  } catch (error) {
    console.error("Unable to parse attachment payload:", error);
  }

  return null;
};

const formatFileSize = (size?: number) => {
  if (!size) {
    return "";
  }

  const units = ["B", "KB", "MB", "GB"];
  let currentSize = size;
  let unitIndex = 0;

  while (currentSize >= 1024 && unitIndex < units.length - 1) {
    currentSize /= 1024;
    unitIndex += 1;
  }

  return `${currentSize.toFixed(currentSize >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const ChatHistory: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const visibleMessages = Array.isArray(messages) ? messages : [];

  const id_user = parseInt(localStorage.getItem("userid") ?? "0");

  const params = useParams<{ id_received?: string; id_chat?: string }>();

  const chatId = parseInt(params.id_chat ?? "0");

  const [idReceived, setIdReceived] = useState<number>(
    parseInt(params.id_received ?? "0")
  );

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const socketUrl = process.env.REACT_APP_SOCKET_URL ?? "http://localhost:5002";

  const [contact, setContact] = useState<Contact | undefined>(undefined);
  const [isSidebarRightOpen, setIsSidebarRightOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const didInitialScrollRef = useRef(false);
  const socketRef = useRef(
    io(socketUrl, {
      transports: ["websocket"],
      withCredentials: true,
    })
  );

  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    const chatBody = chatBodyRef.current;
    if (!chatBody) return;

    chatBody.scrollTo({
      top: chatBody.scrollHeight,
      behavior,
    });
  };

  const keepChatPinnedToBottom = () => {
    window.requestAnimationFrame(() => {
      scrollToBottom("auto");
    });

    window.setTimeout(() => {
      scrollToBottom("auto");
    }, 50);

    window.setTimeout(() => {
      scrollToBottom("smooth");
    }, 150);
  };

  const scrollToBottomAfterRender = (behavior: ScrollBehavior = "smooth") => {
    window.requestAnimationFrame(() => {
      scrollToBottom(behavior);
    });

    window.setTimeout(() => {
      scrollToBottom(behavior);
    }, 80);
  };

  const resolveAttachmentUrl = (fileUrl: string) => {
    if (/^https?:\/\//i.test(fileUrl)) {
      return fileUrl;
    }

    const baseUrl = (backendUrl ?? window.location.origin).replace(/\/$/, "");
    return `${baseUrl}${fileUrl.startsWith("/") ? fileUrl : `/${fileUrl}`}`;
  };

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    if (idReceived) {
      void getConversationMessages(chatId, idReceived);
      return;
    }

    void getMessages(chatId);
  }, [chatId, idReceived]);

  useEffect(() => {
    if (params.id_received) {
      setIdReceived(parseInt(params.id_received));
    }
  }, [params.id_received]);

  useEffect(() => {
    didInitialScrollRef.current = false;
  }, [chatId, idReceived]);

  useEffect(() => {
    const socket = socketRef.current;

    socket.on("connect", () => {
      // console.log('Successfully connected to the server');
      socket.emit("registerUser", id_user);
    });

    // Recevoir des messages du serveur
    socket.on("receive_message", (newMessage: Message) => {
      newMessage = { ...newMessage, is_right: false };

      if (newMessage.id_sender && newMessage.id_sender !== id_user) {
        setMessages((prevMessages) => [
          ...normalizeMessagesPayload(prevMessages),
          newMessage,
        ]);

        keepChatPinnedToBottom();
      }
    });

    return () => {
      socket.off("receive_message");
    };
  }, [id_user, socketRef]);

  useLayoutEffect(() => {
    if (loading || visibleMessages.length === 0) return;

    keepChatPinnedToBottom();
  }, [chatId, idReceived, loading, visibleMessages.length]);
  const sendMessageThroughSocket = (content: string) => {
    const newMessage: Message = {
      content,
      time: new Date().toISOString(),
      is_right: false,
      id_sender: id_user ?? 0,
      id_received: idReceived ?? 0,
      id_chat: chatId,
    };

    const socket = socketRef.current;
    socket.emit("send_private_message", newMessage);

    const outgoingMessage = { ...newMessage, is_right: true };

    setMessages((prevMessages) => [
      ...normalizeMessagesPayload(prevMessages),
      outgoingMessage,
    ]);

    socket.emit("message", { ...outgoingMessage, contact });

    keepChatPinnedToBottom();

    scrollToBottomAfterRender("smooth");
  };
  const uploadAttachment = async (file: File) => {
    const validationError = validateChatAttachment(file);

    if (validationError) {
      throw new Error(validationError);
    }

    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${backendUrl}/api/chat/upload`, {
      method: "POST",
      body: formData,
      mode: "cors",
    });

    const payload = (await response.json().catch(() => null)) as
      | {
          error?: string;
          fileUrl?: string;
          originalName?: string;
          mimeType?: string;
          size?: number;
        }
      | null;

    if (!response.ok || !payload?.fileUrl || !payload?.originalName) {
      throw new Error(
        payload?.error ?? "Impossible de televerser le fichier."
      );
    }

    return payload as {
      fileUrl: string;
      originalName: string;
      mimeType?: string;
      size?: number;
    };
  };

  const handleSendMessage = async (content: string, file?: File | null) => {
    const trimmedContent = content.trim();

    if ((!trimmedContent && !file) || !chatId || !idReceived) {
      if (!chatId || !idReceived) {
        toast.warn("Aucune conversation active.");
      }
      return;
    }

    setIsSending(true);

    try {
      let payloadContent = trimmedContent;

      if (file) {
        const uploadedFile = await uploadAttachment(file);
        payloadContent = `${CHAT_ATTACHMENT_PREFIX}${JSON.stringify({
          type: "file",
          name: uploadedFile.originalName ?? file.name,
          url: uploadedFile.fileUrl,
          mimeType: uploadedFile.mimeType ?? file.type,
          size: uploadedFile.size ?? file.size,
          text: trimmedContent,
        } satisfies AttachmentPayload)}`;
      }

      sendMessageThroughSocket(payloadContent);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Impossible d'envoyer le message."
      );
      throw error;
    } finally {
      setIsSending(false);
    }
  };

  const handleContactClick = (contact: Contact) => {
    console.log("ici");
    if (contact) {
      console.log(contact.id_user);
      setIdReceived(contact.id_user); // ✅ correct
      setContact(contact);
    }
  };

  const toggleSidebarRight = () => {
    setIsSidebarRightOpen(!isSidebarRightOpen);
  };

  const getMessages = async (id_chat: number) => {
    try {
      setLoading(true);

      const bodyData = JSON.stringify({
        id_chat,
      });

      const messagesResponse = await fetch(`${backendUrl}/api/chat/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: bodyData,
        mode: "cors",
      });

      const payload = await messagesResponse.json();
      const data = normalizeMessagesPayload(payload);

      // console.log("data");
      // console.log(data);

      setMessages(data);

      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const getConversationMessages = async (id_chat: number, id_received: number) => {
    try {
      setLoading(true);

      const bodyData = JSON.stringify({
        id_chat,
        id_received
      });

      const messagesResponse = await fetch(`${backendUrl}/api/chat/contact/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: bodyData,
        mode: "cors",
      });

      const payload = await messagesResponse.json();
      const data = normalizeMessagesPayload(payload);

      // console.log("data");
      // console.log(data);

      setMessages(data);

      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);


  // getColorByLetter.ts
  const colors = [
    "bg-primary",
    "bg-success",
    "bg-danger",
    "bg-warning",
    "bg-info",
    "bg-secondary",
    "bg-dark",
  ] as const;

  type BootstrapBgClass = (typeof colors)[number];

  function getColorByLetter(letter: string): BootstrapBgClass {
    const charCode = letter.toUpperCase().charCodeAt(0); // 'A' => 65
    const index = (charCode - 65) % colors.length;
    return colors[index];
  }


  return (
    <>
      <style type="content/css">
        {`
          .dropdown-toggle::after {
            display: none !important;
          }
        `}
      </style>
      <ChatContacts
        handleContactClick={handleContactClick}
        id_contact={id_user}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={toggleSidebar}
      />
      <div
        className="col app-chat-history"
        style={{
          padding: "0",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <div
          className="chat-history-wrapper"
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            height: "100%",
            minHeight: 0,
          }}
        >
          <div
            className="chat-history-header border-bottom"
            style={{ backgroundColor: "#fff" }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex overflow-hidden align-items-center">
                <i className="ti ti-menu-2 ti-lg cursor-pointer d-lg-none d-block me-4"></i>
                <i
                  className="la la-list-alt la-2x ti-lg cursor-pointer me-4"
                  onClick={toggleSidebar}
                />
                {contact?.nom_user ? (
                  <div
                    className="d-flex align-items-center"
                    onClick={toggleSidebarRight}
                  >
                    {contact.img !== "asset/images/logo.png" ? (
                      <img
                        src={`../react/public/${contact.img}`}
                        alt="Avatar"
                        className="rounded-circle border border-light shadow-sm"
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${getColorByLetter(
                          contact.nom_user[0]?.toUpperCase() || "A"
                        )}`}
                        style={{ width: "40px", height: "40px" }}
                      >
                        {contact.nom_user[0]?.toUpperCase()}
                        {contact.prenom_user[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="chat-contact-info flex-grow-1 ms-4">
                      <h6 className="m-0 fw-normal">{contact.nom_user}</h6>
                      {contact.status && (
                        <small className="user-status content-body">
                          {contact.status}
                        </small>
                      )}
                    </div>
                  </div>
                ) : (
                  <h6 className="m-0 fw-normal">{"..."}</h6>
                )}
              </div>
              <div className="d-flex align-items-center">
                <i
                  className="las la-phone-alt cursor-pointer"
                  style={{
                    transition: "background-color 0.3s ease, color 0.3s ease",
                    padding: "10px",
                    borderRadius: "50%",
                    fontSize: "20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff4bf";
                    e.currentTarget.style.color = "#ffd02a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "";
                  }}
                >
                  {" "}
                </i>
                <i
                  className="las la-video cursor-pointer "
                  style={{
                    transition: "background-color 0.3s ease, color 0.3s ease",
                    padding: "10px",
                    borderRadius: "50%",
                    fontSize: "20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff4bf";
                    e.currentTarget.style.color = "#ffd02a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "";
                  }}
                ></i>
                <i
                  className="las la-search"
                  style={{
                    transition: "background-color 0.3s ease, color 0.3s ease",
                    padding: "10px",
                    borderRadius: "50%",
                    fontSize: "20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff4bf";
                    e.currentTarget.style.color = "#ffd02a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "";
                  }}
                ></i>

                <Dropdown>
                  <Dropdown.Toggle
                    as={Button}
                    variant="light"
                    className="icon-wrapper"
                    style={{
                      padding: "0x",
                      borderRadius: "100%",
                      fontSize: "20px",
                      border: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#fff4bf";
                      e.currentTarget.style.color = "#ffd02a";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "";
                    }}
                  >

                  </Dropdown.Toggle>

                  <Dropdown.Menu align="end">
                    <Dropdown.Item href="#" onClick={toggleSidebar}>View Contact</Dropdown.Item>
                    <Dropdown.Item href="#">Mute Notifications</Dropdown.Item>
                    <Dropdown.Item href="#">Block Contact</Dropdown.Item>
                    <Dropdown.Item href="#">Clear Chat</Dropdown.Item>
                    <Dropdown.Item href="#">Report</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>

          <div
            className="chat-history-body"
            ref={chatBodyRef}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              height: "auto",
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            <ul
              className="list-unstyled chat-history"
              style={{ height: "100%", marginBottom: 0 }}
            >
              {loading ? (
                <div style={{ textAlign: "center" }}>
                  <p>
                    <PropagateLoader
                      color={"#123abc"}
                      loading={loading}
                      size={20}
                    />
                  </p>
                </div>
              ) : visibleMessages.length > 0 ? (
                visibleMessages.map((message) => {
                  const attachment = parseAttachmentPayload(message.content);
                  const attachmentUrl = attachment
                    ? resolveAttachmentUrl(attachment.url)
                    : "";

                  return (
                    <li
                      key={
                        message.id_message ??
                        `${message.id_sender}-${message.time}`
                      }
                      className={`chat-message ${message.is_right
                        ? "chat-message-right"
                        : "chat-message-left"
                        }`}
                      style={{ marginBottom: "0px" }}
                    >
                      <div className="d-flex overflow-hidden">
                        {message.nom_user &&
                          message.prenom_user &&
                          !message.is_right ? (
                          <div className="user-avatar flex-shrink-0">
                            {message.img !== "asset/images/logo.png" ? (
                              <img
                                src={`../react/public/${message.img}`}
                                alt="Avatar"
                                className="rounded-circle border border-light shadow-sm"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${getColorByLetter(
                                  message.nom_user?.toUpperCase() || "A"
                                )}`}
                                style={{ width: "40px", height: "40px" }}
                              >
                                {message.nom_user[0]?.toUpperCase()}
                                {message.prenom_user[0]?.toUpperCase()}
                              </div>
                            )}
                          </div>
                        ) : null}
                        <div
                          className="chat-message-wrapper flex-grow-1"
                          style={{ margin: "0 10px" }}
                        >
                          <div className="chat-message-content">
                            {attachment ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.5rem",
                                }}
                              >
                                {attachment.text ? (
                                  <p
                                    className={`mb-0 ${message.is_right ? "is-right" : "is-left"
                                      }`}
                                  >
                                    {attachment.text}
                                  </p>
                                ) : null}
                                <a
                                  href={attachmentUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "0.75rem",
                                    padding: "0.85rem 1rem",
                                    borderRadius: "0.9rem",
                                    background: message.is_right
                                      ? "#e9f2ff"
                                      : "#f4f5f7",
                                    color: "#111827",
                                    textDecoration: "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.75rem",
                                      minWidth: 0,
                                    }}
                                  >
                                    <i
                                      className="las la-paperclip"
                                      style={{ fontSize: "1.2rem" }}
                                    ></i>
                                    <div
                                      style={{
                                        minWidth: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                      }}
                                    >
                                      <strong
                                        style={{
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {attachment.name}
                                      </strong>
                                      <small style={{ color: "#6b7280" }}>
                                        {[attachment.mimeType, formatFileSize(attachment.size)]
                                          .filter(Boolean)
                                          .join(" | ")}
                                      </small>
                                    </div>
                                  </div>
                                  <i className="las la-download"></i>
                                </a>
                              </div>
                            ) : (
                              <p
                                className={`mb-1 ${message.is_right ? "is-right" : "is-left"
                                  }`}
                              >
                                {message.content}
                              </p>
                            )}
                          </div>
                          <div
                            className={`content-${message.is_right ? "end" : "start"
                              } content-muted mt-1`}
                          >
                            {message.is_right ? (
                              <i
                                className={`ti ti-checks ti-16px me-1 content-success`}
                              ></i>
                            ) : (
                              ""
                            )}
                            <small style={{ color: "#000" }}>
                              {<i className="las la-check-double me-1"></i>}{" "}
                              {getMessageDayStatus(message.time)}
                            </small>
                          </div>
                        </div>
                        {message.nom_user &&
                          message.prenom_user &&
                          message.is_right ? (
                          <div className="user-avatar flex-shrink-0">
                            {message.img !== "asset/images/logo.png" ? (
                              <img
                                src={`../react/public/${message.img}`}
                                alt="Avatar"
                                className="rounded-circle border border-light shadow-sm"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold ${getColorByLetter(
                                  message.nom_user?.toUpperCase() || "A"
                                )}`}
                                style={{ width: "40px", height: "40px" }}
                              >
                                {message.nom_user[0]?.toUpperCase()}
                                {message.prenom_user[0]?.toUpperCase()}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })
              ) : (
                <div
                  className="no-messages d-flex justify-content-center align-items-center"
                  style={{
                    textAlign: "center",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <span>No messages available</span>
                </div>
              )}
            </ul>

            <div ref={chatEndRef} />
          </div>

          <div
            className="chat-history-footer"
            style={{
              margin: 0,
              marginTop: "auto",
              padding: "0.75rem 1rem",
              borderTop: "1px solid #e9ecef",
              backgroundColor: "#fff",
              flexShrink: 0,
              position: "sticky",
              bottom: 0,
              zIndex: 1,
            }}
          >
            <ChatInput
              onSendMessage={handleSendMessage}
              isSending={isSending}
            />
          </div>
        </div>
        <div
          className={`col app-chat-sidebar-right app-sidebar overflow-hidden ${isSidebarRightOpen ? "sidebar-right-open" : ""
            }`}
          id="app-chat-sidebar-right"
          style={{ background: "#fff", border: "1px solid #ddd" }}
        >
          <div
            className="sidebar-header d-flex flex-column justify-content-center align-items-center flex-wrap px-6 pt-12"
            onClick={toggleSidebarRight}
            style={{ margin: "15px 0", height: "108px !important" }}
          >
            <div
              className="avatar avatar-xl avatar-online chat-sidebar-avatar"
              onClick={toggleSidebarRight}
            >
              <img
                src="../asset/images/avatars/4.png"
                alt="Avatar"
                className="rounded-circle"
                style={{ height: "41px" }}
              />
            </div>
            <h5 className="mt-4 mb-0">Felecia Rower</h5>
            <span>NextJS Developer</span>
            <i
              className="ti ti-x ti-lg cursor-pointer close-sidebar"
              onClick={toggleSidebarRight}
            ></i>
          </div>
          <div className="sidebar-body p-6 pt-0">
            <div className="my-6">
              <p className="content-uppercase mb-1 content-muted">About</p>
              <p className="mb-0">
                It is a long established fact that a reader will be distracted
                by the readable content.
              </p>
            </div>
            <div className="my-6">
              <p className="content-uppercase mb-1 content-muted">
                Personal Information
              </p>
              <ul className="list-unstyled d-grid gap-4 mb-0 ms-2 py-2 content-heading">
                <li className="d-flex align-items-center">
                  <i className="ti ti-mail ti-md"></i>
                  <span className="align-middle ms-2">
                    josephGreen@email.com
                  </span>
                </li>
                <li className="d-flex align-items-center">
                  <i className="ti ti-phone-call ti-md"></i>
                  <span className="align-middle ms-2">+1(123) 456 - 7890</span>
                </li>
                <li className="d-flex align-items-center">
                  <i className="ti ti-clock ti-md"></i>
                  <span className="align-middle ms-2">
                    Mon - Fri 10AM - 8PM
                  </span>
                </li>
              </ul>
            </div>
            <div className="my-6">
              <p className="content-uppercase content-muted mb-1">Options</p>
              <ul className="list-unstyled d-grid gap-4 ms-2 py-2 content-heading">
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-badge ti-md"></i>
                  <span className="align-middle ms-2">Add Tag</span>
                </li>
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-star ti-md"></i>
                  <span className="align-middle ms-2">Important Contact</span>
                </li>
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-photo ti-md"></i>
                  <span className="align-middle ms-2">Shared Media</span>
                </li>
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-trash ti-md"></i>
                  <span className="align-middle ms-2">Delete Contact</span>
                </li>
                <li className="cursor-pointer d-flex align-items-center">
                  <i className="ti ti-ban ti-md"></i>
                  <span className="align-middle ms-2">Block Contact</span>
                </li>
              </ul>
            </div>
            <div className="d-flex mt-6">
              <button className="btn btn-danger w-100">
                Delete Contact<i className="ti ti-trash ti-16px ms-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatHistory;
